<template>
  <CustomModal ref="modal" title="Import des factures reçues" class="import-result-modal">
    <div class="container">
      <p v-if="summary && !summary.refreshed" class="warning">
        Les nouvelles factures n'ont pas pu être redemandées à la plateforme : seules les factures déjà connues ont été importées.
      </p>
      <ul v-if="summary" class="counters">
        <li>
          <strong>{{ summary.imported }}</strong> facture(s) importée(s) en achats à vérifier
          <template v-if="summary.importedCreditNotes > 0"> (dont <strong>{{ summary.importedCreditNotes }}</strong> avoir(s) fournisseur)</template>
        </li>
        <li><strong>{{ summary.createdSuppliers }}</strong> fournisseur(s) créé(s)</li>
        <li><strong>{{ summary.alreadyImported }}</strong> facture(s) déjà importée(s)</li>
        <li><strong>{{ summary.ignored.length }}</strong> facture(s) ignorée(s)</li>
      </ul>
      <p v-if="summary && summary.imported > 0" class="note">
        Les achats importés sont « à vérifier » : contrôlez-les, complétez la catégorie et les dossiers concernés, puis validez-les pour pouvoir enregistrer leur paiement.
      </p>
      <div v-if="summary && summary.ignored.length > 0" class="ignored">
        <p class="section-label">Factures ignorées</p>
        <ul>
          <li v-for="(invoice, index) in summary.ignored" :key="index">
            <span class="number">{{ invoice.invoiceNumber || 'Sans numéro' }}</span>
            <span class="reason">
              {{ invoice.reason }}
              <span v-if="invoice.details" class="details" :title="invoice.details">{{ invoice.details }}</span>
            </span>
          </li>
        </ul>
      </div>
      <div v-if="summary && summary.warnings && summary.warnings.length > 0" class="ignored">
        <p class="section-label">Importées avec des valeurs par défaut (à vérifier)</p>
        <ul>
          <li v-for="(warning, index) in summary.warnings" :key="index">
            <span class="number">{{ warning.invoiceNumber || 'Sans numéro' }}</span>
            <span class="reason"><span class="details" :title="warning.message">{{ warning.message }}</span></span>
          </li>
        </ul>
      </div>
      <p v-if="hasDiagnostic" class="note">
        Une facture n'a pas été lue comme prévu ? Copiez le diagnostic (noms des champs uniquement, aucune valeur) et transmettez-le au support pour que le format de ce fournisseur soit pris en compte.
      </p>
      <div class="button-container">
        <CustomButton v-if="hasDiagnostic" class="button" @click="copyDiagnostic()" :params=buttons.copy :isSecondary="true" />
        <CustomButton class="button" @click="closeModal()" :params=buttons.close />
      </div>
    </div>
  </CustomModal>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {NotificationService} from "@vues/v-common";

// Résultat de l'import des factures reçues : à ouvrir avec openModal(résumé renvoyé par ms-purchase)
export default {
  name: 'PurchaseImportResultModal',
  components: {CustomModal, CustomButton},
  data() {
    return {
      summary: undefined,
      buttons: {
        close: {text: "Fermer"},
        copy: {text: "Copier le diagnostic"},
      }
    };
  },
  computed: {
    hasDiagnostic() {
      return !!this.summary && ((this.summary.ignored || []).some(invoice => invoice.details) || (this.summary.warnings || []).length > 0);
    }
  },
  methods: {
    // Texte à transmettre au support : raisons détaillées et structure des factures (noms de champ, sans valeurs)
    buildDiagnostic() {
      const lines = [];
      (this.summary.ignored || []).filter(invoice => invoice.details).forEach(invoice => {
        lines.push(`Facture ignorée ${invoice.invoiceNumber || '(sans numéro)'} : ${invoice.reason}`, invoice.details);
        if (invoice.structure && invoice.structure.length > 0) lines.push('Structure :', ...invoice.structure.map(field => `  ${field}`));
        lines.push('');
      });
      (this.summary.warnings || []).forEach(warning => lines.push(`Facture importée ${warning.invoiceNumber || '(sans numéro)'} : ${warning.message}`));
      return lines.join('\n').trim();
    },
    async copyDiagnostic() {
      try {
        await navigator.clipboard.writeText(this.buildDiagnostic());
        (new NotificationService).addNotification("success", "Diagnostic copié");
      } catch (e) {
        (new NotificationService).addNotification("danger", "Copie impossible : autorisez l'accès au presse-papiers");
      }
    },
    openModal(summary) {
      this.summary = summary;
      this.$refs.modal.openModal();
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.container
  display flex
  flex-direction column
  gap 10px

  .warning
    margin 0
    padding 8px 10px
    border-radius 6px
    background #fff3cd
    font-size 0.9em

  .counters
    margin 0
    padding-left 18px
    line-height 1.6

  .note
    margin 0
    font-size 0.85em
    color #6b7280

  .ignored
    padding-top 10px
    border-top 1px solid #eef0f2

    .section-label
      margin 0 0 6px
      font-size 9.5px
      font-weight 700
      letter-spacing 0.05em
      text-transform uppercase
      color #b4b8bd

    ul
      margin 0
      padding 0
      list-style none
      max-height 180px
      overflow-y auto

      li
        display flex
        gap 10px
        padding 2px 0
        font-size 0.9em

        .number
          font-weight 600
          white-space nowrap

        .reason
          color #6b7280
          min-width 0

          .details
            display -webkit-box
            -webkit-line-clamp 2
            -webkit-box-orient vertical
            overflow hidden
            font-size 0.85em
            color #9ca3af

  .button-container
    display flex
    justify-content flex-end
    margin-top 4px

    .button
      min-width 110px
</style>

<style lang="stylus">
.import-result-modal
  > .modal
    width 680px !important
    max-width 100%
    height auto !important
    max-height 90vh
</style>
